import { ReactNode } from 'react';
import { ThemeProvider, Global, css } from '@emotion/react';
import { useTheme } from '../context/ThemeContext';
import { lightTheme } from './lightTheme';
import { darkTheme } from './darkTheme';

interface EmotionThemeProviderProps {
  children: ReactNode;
}

export function EmotionThemeProvider({ children }: EmotionThemeProviderProps) {
  const { isDark } = useTheme();
  const emotionTheme = isDark ? darkTheme : lightTheme;

  return (
    <ThemeProvider theme={emotionTheme}>
      <Global
        styles={css`
          /* CSS Reset */
          *,
          *::before,
          *::after {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }

          /* Body defaults */
          body {
            background-color: ${emotionTheme.background.secondary}; /* canvas-soft — page bg */
            color: ${emotionTheme.font.color.primary};
            font-family: ${emotionTheme.font.family};
            font-size: ${emotionTheme.font.size.md};
            line-height: ${emotionTheme.font.lineHeight.lg};
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }

          /* Default tabular numerics on every table cell. Pages can opt out
             with .text-cell on the Td when they need proportional figures. */
          table td {
            font-feature-settings: "tnum";
            font-variant-numeric: tabular-nums;
          }
          table td.text-cell {
            font-feature-settings: normal;
            font-variant-numeric: normal;
          }

          /* Utility for any non-table cell that needs to align numbers */
          .tabular {
            font-feature-settings: "tnum";
            font-variant-numeric: tabular-nums;
          }

          /* Focus ring for accessibility */
          :focus-visible {
            outline: 2px solid ${emotionTheme.accent.primary};
            outline-offset: 2px;
          }

          /* Text selection */
          ::selection {
            background-color: ${emotionTheme.accent.tertiary};
            color: ${emotionTheme.font.color.primary};
          }

          /* Custom scrollbars (webkit) */
          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }

          ::-webkit-scrollbar-track {
            background: ${emotionTheme.background.tertiary};
          }

          ::-webkit-scrollbar-thumb {
            background: ${emotionTheme.border.color.medium};
            border-radius: 4px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: ${emotionTheme.border.color.strong};
          }

          /* Theme switch transition is opt-in per component; the universal
             rule was causing ripple-like animations on every state change. */

          /* Headings */
          h1,
          h2,
          h3,
          h4,
          h5,
          h6 {
            color: ${emotionTheme.font.color.primary};
            font-weight: ${emotionTheme.font.weight.semiBold};
            line-height: ${emotionTheme.font.lineHeight.md};
          }

          /* Links */
          a {
            color: ${emotionTheme.accent.primary};
            text-decoration: none;

            &:hover {
              text-decoration: underline;
            }
          }

          /* Loading state */
          .loading {
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            font-size: ${emotionTheme.font.size.lg};
            color: ${emotionTheme.font.color.secondary};
          }
        `}
      />
      {children}
    </ThemeProvider>
  );
}
